import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Session } from 'api/types/session';
import { PaginatedResponse } from 'api/types/pagination';
import SessionsApi from 'api/sessions';

interface SessionState {
  sessions: Session[];
  loading: boolean;
  error?: string;
  pagination: {
    currentPage: number;
    pageSize: number;
    totalPages: number;
    totalRecords: number;
    hasMore: boolean;
  };
  sorting: {
    sortBy: 'created_date' | 'name_asc' | 'name_desc' | 'ended_date';
  };
  lastFetchedContext?: string;
}

const initialState: SessionState = {
  sessions: [],
  loading: false,
  error: undefined,
  pagination: {
    currentPage: 1,
    pageSize: 25,
    totalPages: 0,
    totalRecords: 0,
    hasMore: false
  },
  sorting: {
    sortBy: 'created_date'
  },
  lastFetchedContext: undefined
};

export const fetchSessionsByCampaignId = createAsyncThunk<
  PaginatedResponse<Session>,
  { campaignId: string; limit?: number; page?: number; sortBy?: string; source?: string },
  { rejectValue: string }
>(
  'session/fetchSessionsByCampaignId',
  async ({ campaignId, limit = 10, page = 1, sortBy, source }, { rejectWithValue }) => {
    try {
      const response = await SessionsApi.fetchSessionsByCampaignId(
        campaignId,
        limit,
        page,
        sortBy,
        source
      );
      if (!response.data) {
        throw new Error('No data found');
      }
      if (response.status !== 200) {
        throw new Error(`Error: ${response.status}`);
      }

      return response.data;
    } catch (err: unknown) {
      let errorMessage = 'Failed to fetch sessions';
      if (err instanceof Error) {
        errorMessage = err.message;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateSession = createAsyncThunk<
  Session,
  { id: string; data: Partial<Session> },
  { rejectValue: string }
>('session/updateSession', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await SessionsApi.updateSession(id, data);
    if (!response.data) {
      throw new Error('No data found');
    }
    if (response.status !== 200) {
      throw new Error(`Error: ${response.status}`);
    }
    return response.data;
  } catch (err: unknown) {
    let errorMessage = 'Failed to update session';
    if (err instanceof Error) {
      errorMessage = err.message;
    }
    return rejectWithValue(errorMessage);
  }
});

const sessionSlice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.pagination.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pagination.pageSize = action.payload;
      state.pagination.currentPage = 1; // Reset to first page when page size changes
    },
    setSortBy: (
      state,
      action: PayloadAction<'created_date' | 'name_asc' | 'name_desc' | 'ended_date'>
    ) => {
      state.sorting.sortBy = action.payload;
      state.pagination.currentPage = 1; // Reset to first page when sorting changes
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSessionsByCampaignId.pending, (state, action) => {
        state.loading = true;
        state.error = undefined;
        // Reset pagination if fetching for a different campaign
        const currentContext = `${action.meta.arg.campaignId}-sessions-list`;
        if (state.lastFetchedContext && state.lastFetchedContext !== currentContext) {
          state.pagination.currentPage = 1;
        }
      })
      .addCase(fetchSessionsByCampaignId.fulfilled, (state, action) => {
        state.loading = false;
        state.sessions = action.payload.data;
        state.pagination = {
          currentPage: action.payload.currentPage,
          pageSize: action.payload.pageSize,
          totalPages: action.payload.totalPages,
          totalRecords: action.payload.totalRecords,
          hasMore: action.payload.hasMore
        };
        state.lastFetchedContext = `${action.meta.arg.campaignId}-sessions-list`;
      })
      .addCase(fetchSessionsByCampaignId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Something went wrong';
      });

    builder
      .addCase(updateSession.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(updateSession.fulfilled, (state, action: PayloadAction<Session>) => {
        state.loading = false;
        const index = state.sessions.findIndex((session) => session.id === action.payload.id);
        if (index !== -1) {
          state.sessions[index] = { ...state.sessions[index], ...action.payload };
        }
      })
      .addCase(updateSession.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Something went wrong';
      });
  }
});

export const { setCurrentPage, setPageSize, setSortBy } = sessionSlice.actions;
export default sessionSlice.reducer;
